import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { Mutex } from 'async-mutex';
import { take } from 'rxjs/operators';
import { ConfigConstants } from '../config/config-constants';
import { FileStorageInjectionToken, FileStorageServiceInterface } from '../interfaces/file-storage.interface';
import { DataMaintenanceLocationP58Model } from '../models/complex/data-maintenance-location-p58.model';
import { DataMaintenanceSystemConfigP58Model } from '../models/complex/data-maintenance-system-config-p58.model';
import { RecipientModel } from '../models/low-level/recipient.model';
import { ConfigService } from '../services/config/config.service';
import { LocalStorageRecipientsService } from '../services/local-storage/local-storage-recipients.service';
import { WindowService } from '../services/window/window.service';
import { isDefined } from './is-defined';

@Injectable({
    providedIn: 'root',
})
export class DataMaintenanceUtilities {
    private readonly recipientMutex: Mutex;
    private window: Window;

    public constructor(
        private readonly configService: ConfigService,
        @Inject(FileStorageInjectionToken) private readonly fileStorage: FileStorageServiceInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageRecipientsService: LocalStorageRecipientsService,
        private readonly translate: TranslateService,
        private readonly windowService: WindowService,
    ) {
        this.window = windowService.nativeWindow;
        this.recipientMutex = new Mutex();
    }

    public isEmailInvalid(email: string, pattern: string): boolean {
        return isDefined(email) && email.length > 0 && email.match(pattern) === null;
    }

    public updateRecipientListInLocalStorage(model: DataMaintenanceLocationP58Model): void {
        model.recipients.value.map((recipient) => {
            recipient.isDeletable = !recipient.isFromEmailSet;
            recipient.isEditable = !recipient.isFromEmailSet;
            recipient.isSubmitted = true;
        });
        const cseEmail = this.configService.getCseEmailFax();
        model.recipients.value.unshift(
            new RecipientModel(
                cseEmail,
                ConfigConstants.confirmationServiceReportCommCategory,
                true,
                true,
                false,
                false,
                false,
                true,
            ),
        );
        this.localStorageRecipientsService
            .saveRecipientsByNotificationId(model.recipients.value, model.notificationIdentifier)
            .pipe(take(1))
            .subscribe();
    }

    public openMailToSubmitData(
        model: DataMaintenanceLocationP58Model | DataMaintenanceSystemConfigP58Model,
        oldModel: DataMaintenanceLocationP58Model | DataMaintenanceSystemConfigP58Model,
        isInvitro: boolean,
    ): void {
        const emailId = isInvitro
            ? this.configService.getDataManagementEmailInvitro()
            : this.configService.getDataManagementEmail();

        // Subject: Change data from PACE: Username | 60000025627(0001) | 004-526178
        const emailSubject = `${<string>(
            this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_EMAIL_SUBJECT')
        )} | ${
            model.name1.value
        } | ${model.notificationIdentifier.getQmnum()}(${model.notificationIdentifier.getManum()}) | ${
            model.functionalLoc.value
        }`;

        const emailBody = this.getModelValueForMail(model, oldModel);

        this.window.location.href =
            `mailTo:${emailId}?` +
            `subject=${encodeURIComponent(emailSubject)}` +
            `&body=${encodeURIComponent(emailBody)}`;
    }

    public getModelValueForMail(
        model: DataMaintenanceLocationP58Model | DataMaintenanceSystemConfigP58Model,
        old: DataMaintenanceLocationP58Model | DataMaintenanceSystemConfigP58Model,
    ): string {
        let oldValues = '';
        let newValues = '';

        if (model instanceof DataMaintenanceLocationP58Model && old instanceof DataMaintenanceLocationP58Model) {
            const newModel = model.getOutModel();
            const oldModel = old.getOutModel();

            // Only changed value should be sent in mail, so it is necessary to compare old Model values and new Model values
            for (const key in newModel) {
                if (oldModel[key]?.toString().trim() !== newModel[key]?.toString().trim()) {
                    newValues = newValues + `${key} : ${newModel[key]}\n`;
                    oldValues = oldValues + `${key} : ${oldModel[key] ?? '-'}\n`;
                }
            }
            // This below function when an email is deleted from existing model
            // Example: we have Email 1 and Email 2, If Email 2 is deleted then it will be present in newModel
            for (const key in oldModel) {
                if (!newModel.hasOwnProperty(key)) {
                    newValues = newValues + `${key} : -\n`;
                    oldValues = oldValues + `${key} : ${oldModel[key] ?? '-'}\n`;
                }
            }
            return `Old Address:\n` + `${oldValues}\n` + `New Address : \n${newValues}`;
        }
        const newEquipmentModel = <DataMaintenanceSystemConfigP58Model>model;
        const oldEquipmentModel = <DataMaintenanceSystemConfigP58Model>old;

        oldEquipmentModel.equipmentSet.value.forEach((oldModel) => {
            newEquipmentModel.equipmentSet.value.forEach((newModel) => {
                if (
                    oldModel.equnr.value === newModel.equnr.value &&
                    (oldModel.tidnr.value.trim() !== newModel.tidnr.value.trim() ||
                        oldModel.matnr.value.trim() !== newModel.matnr.value.trim() ||
                        oldModel.sernr.value.trim() !== newModel.sernr.value.trim())
                ) {
                    oldValues =
                        oldValues +
                        `\nEquipment Name: ${oldModel.equnr.value}\n${oldModel.tidnr.name} : ${oldModel.tidnr.value}\n${oldModel.matnr.name} : ${oldModel.matnr.value}\n${oldModel.sernr.name} : ${oldModel.sernr.value}\n`;
                    newValues =
                        newValues +
                        `\nEquipment Name: ${oldModel.equnr.value}\n${newModel.tidnr.name} : ${newModel.tidnr.value}\n${newModel.matnr.name} : ${newModel.matnr.value}\n${newModel.sernr.name} : ${newModel.sernr.value}\n`;
                }
            });
        });
        return `Old Equipment:\n` + `${oldValues}\n` + `New Equipment :\n${newValues}`;
    }
}
